import { z } from 'zod'
import { createApiClient, type ApiClient } from '../api/client'

/**
 * A sessao do jogador no mundo (modo_historia/03#HM-002). docs/21 secao 2, ADR 0011.
 *
 * O mundo abre por redirect a partir do `web2`, num subdominio *same-site* do `api`: o cookie de renovacao
 * vai junto, e a sessao se recupera com `POST /auth/refresh`. O access token fica **em memoria**, nunca em
 * `localStorage`, como no `web2`.
 *
 * **O refresh e de uso unico, com rotacao** (docs/10 secao 1.2): apresentar o mesmo token duas vezes revoga
 * a familia inteira. Por isso toda renovacao passa por UMA porta (`renew`), que reaproveita a promessa em voo.
 */

const refreshSchema = z.object({ accessToken: z.string().min(1), expiresIn: z.number().int().positive() })
const meSchema = z.object({ userId: z.string().min(1), nickname: z.string().nullable() })
const accessSchema = z.object({ allowed: z.boolean() })

export type Me = z.infer<typeof meSchema>

/** O que a abertura do mundo descobre, em ordem: sem sessao, sem acesso, falha, ou pronto para entrar. */
export type BootOutcome =
  { kind: 'anonymous' } | { kind: 'denied'; me: Me } | { kind: 'failed' } | { kind: 'ready'; me: Me }

/** Renova um minuto antes de expirar, como o `web2`: sem janela de 401 no meio de uma acao. */
const RENEW_MARGIN_MS = 60_000

export type Scheduler = (delayMs: number, run: () => void) => () => void

export class Session {
  private token: string | null = null
  private inFlight: Promise<boolean> | null = null
  private cancelRenew: (() => void) | null = null
  readonly api: ApiClient

  constructor(
    apiUrl: string,
    fetcher: typeof fetch = (...args) => fetch(...args),
    private readonly schedule: Scheduler = (delay, run) => {
      const id = setTimeout(run, delay)
      return () => clearTimeout(id)
    },
  ) {
    this.api = createApiClient(apiUrl, () => this.token, fetcher)
  }

  get accessToken(): string | null {
    return this.token
  }

  /**
   * Abre o mundo: renova a sessao, descobre quem e e pergunta se entra.
   *
   * 401 no refresh e "sem sessao" -- o jogador vai ao login do `web2`. Qualquer outra falha e "falhou", com
   * tentar de novo: uma rede caida nao pode mandar quem estava logado para a tela de login.
   */
  async boot(): Promise<BootOutcome> {
    const renewed = await this.renewOutcome()
    if (renewed === 'anonymous') return { kind: 'anonymous' }
    if (renewed === 'failed') return { kind: 'failed' }

    const me = await this.api.request('/auth/me', meSchema)
    if (!me.ok) return me.status === 401 ? { kind: 'anonymous' } : { kind: 'failed' }

    const access = await this.api.request('/story/access', accessSchema)
    if (!access.ok) return { kind: 'failed' }
    return access.data.allowed ? { kind: 'ready', me: me.data } : { kind: 'denied', me: me.data }
  }

  /** A unica porta da renovacao. Duas chamadas simultaneas fazem UMA requisicao. */
  renew(): Promise<boolean> {
    this.inFlight ??= this.doRenew().finally(() => {
      this.inFlight = null
    })
    return this.inFlight
  }

  dispose(): void {
    this.cancelRenew?.()
    this.cancelRenew = null
  }

  private lastStatus = 0

  private async renewOutcome(): Promise<'ok' | 'anonymous' | 'failed'> {
    if (await this.renew()) return 'ok'
    return this.lastStatus === 401 ? 'anonymous' : 'failed'
  }

  private async doRenew(): Promise<boolean> {
    const result = await this.api.request('/auth/refresh', refreshSchema, { method: 'POST', body: {} })
    if (!result.ok) {
      this.lastStatus = result.status
      this.token = null
      return false
    }
    this.lastStatus = 200
    this.token = result.data.accessToken
    this.cancelRenew?.()
    const delay = Math.max(RENEW_MARGIN_MS, result.data.expiresIn * 1000 - RENEW_MARGIN_MS)
    this.cancelRenew = this.schedule(delay, () => void this.renew())
    return true
  }
}
